import icon from '@/assets/icon.png'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { FaWindows, FaLinux } from 'react-icons/fa'
import { PiCloudArrowDownDuotone } from 'react-icons/pi'
import { cn } from '@/lib/utils'
import { EmailSignupForm } from './email-signup-form'
import { ExternalLink } from 'lucide-react'
// import { CheckIcon } from 'lucide-react'

type Platform = {
  id: string
  label: string
  icon: React.ElementType
}

const platforms: Platform[] = [
  // { id: 'macos', label: 'macOS', icon: FaApple },
  { id: 'windows', label: 'Windows', icon: FaWindows },
  { id: 'linux', label: 'Linux', icon: FaLinux },
]

export function PlatformsSelectorModal({
  platform = 'windows',
}: {
  platform?: Platform['id']
}) {
  const [open, setOpen] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform['id'][]>([
    platform,
  ])

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((id) => id !== platformId)
        : [...prev, platformId],
    )
  }

  // Map selected platform IDs to comma-separated string for form submission
  const platformValue = useMemo(() => {
    return selectedPlatforms.join(',')
  }, [selectedPlatforms])

  // Find the platform object for the passed-in platform prop
  const selectedPlatform = useMemo(() => {
    return platforms.find((p) => p.id === platform)
  }, [platform])

  const PlatformIcon = selectedPlatform?.icon || PiCloudArrowDownDuotone

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="min-w-[195px] md:min-w-auto">
          <motion.div
            className="bg-card text-card-foreground ring-foreground/1 flex h-14 items-center gap-3 rounded-lg px-6 py-3 shadow-lg ring-1 transition-shadow hover:shadow-xl"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* <PiCloudArrowDownDuotone className="h-6 w-6" />
            <span className="text-sm leading-tight font-semibold">
              Download Direct
            </span> */}
            <PlatformIcon className="h-6 w-6" />
            <div className="flex-start flex flex-col text-left">
              <span className="text-[10px] leading-tight">Download for</span>
              <span className="text-sm leading-tight font-semibold">
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </span>
            </div>
          </motion.div>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-foreground/5 ring-foreground/5 max-w-md gap-0 rounded-2xl border-none p-[2px] shadow-lg ring-1 backdrop-blur-xl">
        <DialogHeader className="p-2">
          <div className="flex w-full flex-col">
            <DialogTitle className="flex items-center text-sm font-medium">
              <div>macOS, Windows and Linux </div>
            </DialogTitle>
            <DialogDescription className="text-foreground/60 sr-only p-2 text-xs">
              Get notified when picchat.ai is available for your platform.
            </DialogDescription>
          </div>
        </DialogHeader>
        {/* <div className="bg-background dark:bg-foreground/5 ring-foreground/15 flex w-full max-w-full flex-1 flex-col gap-4 rounded-2xl p-8 ring-1"> */}
        <div className="bg-background dark:bg-background/85 ring-foreground/15 flex w-full max-w-full flex-1 flex-col gap-4 rounded-2xl p-8 ring-1">
          <div className="flex w-full items-start gap-4">
            <div className="relative flex w-max items-center justify-center">
              <img src={icon} className="h-auto w-24" alt="picchat.ai" />
            </div>
            <p className="text-foreground/60 text-sm text-balance">
              {/* <span className="font-semibold">picchat.ai</span> will be
              available for macOS, Windows and Linux. Get notified when it's
              ready for your platform. */}
              <span className="font-semibold">picchat.ai</span> is available on
              the{' '}
              <a
                href="https://apps.apple.com/app/picchat-ai/id6743186306"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1"
              >
                <span className="text-foreground">Mac App Store</span>
                <ExternalLink className="inline-block size-3" />.
              </a>
              It will be available for Windows and Linux soon. Get notified when
              it's ready for your platform.
            </p>
          </div>
          <EmailSignupForm
            defaultPlatform="MAS"
            platform={platformValue}
            platformSelectionUI={
              <div className="flex w-full flex-col gap-2">
                <Label className="sr-only text-sm font-medium">
                  Select your platform
                </Label>
                <div className="flex flex-wrap gap-2">
                  {platforms.map((platform) => {
                    const Icon = platform.icon
                    const isSelected = selectedPlatforms.includes(platform.id)
                    return (
                      <button
                        key={platform.id}
                        type="button"
                        onClick={() => togglePlatform(platform.id)}
                        className={cn(
                          'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all',
                          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                          isSelected
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-foreground/20 bg-foreground/5 text-foreground/60 hover:bg-foreground/10 hover:text-foreground',
                        )}
                      >
                        {/* {isSelected && (
                          <div className="bg-background text-foreground flex size-4 items-center justify-center rounded-full">
                            <CheckIcon className="size-3" />
                          </div>
                        )} */}
                        <Icon className="size-4" />
                        <span>{platform.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            }
            onSubmit={() => setOpen(false)}
          />
        </div>
        <DialogFooter className="flex flex-col gap-2 p-2 sm:flex-row sm:items-center sm:justify-between">
          <div></div>
          <Button
            type="submit"
            form="email-signup-form"
            className="rounded-full"
          >
            <span>Get notified</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
